import { NgClass } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, numberAttribute } from '@angular/core';
import { BadgeDirective } from './badge.directive';
import { ProgressComponent } from './progress.component';

export const trackerBatteryColorClass = (level: number) => {
  if (level > 50) {
    return 'bg-green-100 text-green-800';
  } else if (level > 30) {
    return 'bg-yellow-100 text-yellow-800';
  } else if (level !== 0) {
    return 'bg-red-100 text-red-800';
  } else {
    return 'bg-gray-100 text-gray-800';
  }
};

/** Fill colour of the battery glyph per level — same thresholds as the bar and the badge. */
const ICON_FILL: (level: number) => string = level => {
  if (level > 50) return 'bg-emerald-500';
  if (level > 30) return 'bg-amber-400';
  if (level !== 0) return 'bg-red-500';
  return 'bg-gray-300 dark:bg-slate-600';
};

/**
 * Battery level, three renderings:
 * - `bar` (default): the historical progress bar;
 * - `isBadge`: "Batterie : 66%" badge;
 * - `variant="icon"`: a battery glyph filled to the level, followed by the percentage
 *   (devices list). Percentage shown with `showPercent` in bar mode too.
 */
@Component({
  selector: 'lib-battery',
  standalone: true,
  imports: [ProgressComponent, BadgeDirective, NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isBadge()) {
      <span libBadge [ngClass]="trackerBatteryColorClass(value())">
        Batterie :
        {{ value() + '%' }}
      </span>
    } @else if (variant() === 'icon') {
      <span class="inline-flex items-center gap-2" [title]="value() + ' %'">
        <span class="relative inline-flex h-[12px] w-[24px] items-center" aria-hidden="true">
          <span class="absolute inset-0 rounded-[3px] border-[1.5px] border-gray-400 dark:border-slate-400"></span>
          <span class="absolute -right-[3px] top-[3px] h-[6px] w-[2px] rounded-r-[1px] bg-gray-400 dark:bg-slate-400"></span>
          <span class="absolute bottom-[2px] left-[2px] top-[2px] rounded-[1px]" [class]="fillClass()" [style.width.%]="fillPercent()"></span>
        </span>
        @if (showPercent()) {
          <span class="text-xs tabular-nums text-slate-700 dark:text-slate-200">{{ value() === 0 ? '—' : value() + ' %' }}</span>
        }
      </span>
    } @else {
      <span class="inline-flex w-full items-center gap-2">
        <span class="min-w-0 flex-1">
          @if (value() > 50) {
            <lib-progress [value]="value()" class="text-green-800" progressColor="bg-green-100" />
          } @else if (value() > 30) {
            <lib-progress [value]="value()" class="text-yellow-800" progressColor="bg-yellow-200" />
          } @else if (value() !== 0) {
            <lib-progress [value]="value()" class="text-red-800" progressColor="bg-red-200" />
          } @else {
            <lib-progress [value]="value()" class="text-gray-800" progressColor="bg-gray-200" />
          }
        </span>
        @if (showPercent()) {
          <span class="shrink-0 text-xs tabular-nums text-slate-700 dark:text-slate-200">{{ value() === 0 ? '—' : value() + ' %' }}</span>
        }
      </span>
    }
  `,
})
export class BatteryComponent {
  trackerBatteryColorClass = trackerBatteryColorClass;
  value = input.required({ transform: numberAttribute });
  isBadge = input(false, { transform: booleanAttribute });
  /** "66 %" next to the bar or the glyph. Off by default, existing usages unchanged. */
  showPercent = input(false, { transform: booleanAttribute });
  /** `bar` (historical) or `icon` (battery glyph filled to the level). */
  variant = input<'bar' | 'icon'>('bar');

  /** Glyph inner width: 0–100 % of the usable area, clamped; an unknown level shows empty. */
  protected readonly fillPercent = computed(() => Math.max(0, Math.min(100, this.value() || 0)) * 0.92);
  protected readonly fillClass = computed(() => ICON_FILL(this.value() || 0));
}
